import type { BotSelect, PromptSelect } from '@erikdakoda/database/types';

/** Scalar prompt fields aligned with `PromptForAiToolsSchema` (no nested childPrompts). */
export const promptForEditLeafSelect = {
  id: true,
  name: true,
  slug: true,
  createdAt: true,
  updatedAt: true,
  publishedAt: true,
  private: true,
  ownerId: true,
  spaceId: true,
  description: true,
  group: true,
  handle: true,
  variables: true,
  script: true,
  text: true,
} satisfies Pick<
  PromptSelect,
  | 'id'
  | 'name'
  | 'slug'
  | 'createdAt'
  | 'updatedAt'
  | 'publishedAt'
  | 'private'
  | 'ownerId'
  | 'spaceId'
  | 'description'
  | 'group'
  | 'handle'
  | 'variables'
  | 'script'
  | 'text'
>;

/**
 * Nested child-prompt join shape for edit selects.
 * Kept as a structural literal (not `PromptSelect['childPrompts']`) so ZenStack 3
 * `QueryRelevantOptions` nesting stays assignable through recursive depth.
 */
type PromptForEditChildPromptsSelect = {
  orderBy: { ord: 'asc' };
  select: {
    id: true;
    ord: true;
    prompt: {
      select: PromptForEditNestedSelect;
    };
  };
};

// The recursion forces a NAMED return type here (TS cannot infer a directly recursive
// function's type). Building it off `typeof promptForEditLeafSelect` rather than
// `Pick<PromptSelect, …>` keeps the leaf keys as required `true` literals, so
// `PromptGetPayload` yields required scalars instead of an all-optional payload.
// `childPrompts` is genuinely optional — the depth-0 leaf omits it.
export type PromptForEditNestedSelect = typeof promptForEditLeafSelect & {
  childPrompts?: PromptForEditChildPromptsSelect;
};

/** Nested child-prompt depth for read/edit AI tool payloads (matches prior select shapes). */
export const PROMPT_FOR_EDIT_CHILD_DEPTH = 3;

/**
 * Builds a prompt select with nested child prompts for read/edit AI tools.
 * @param nestedChildPromptDepth Number of childPrompts levels before a leaf prompt.
 * @returns Prompt select object aligned with `PromptForAiToolsSchema` validation.
 */
export function buildPromptForEditNestedSelect(
  nestedChildPromptDepth: number,
): PromptForEditNestedSelect {
  if (nestedChildPromptDepth <= 0) {
    return promptForEditLeafSelect;
  }

  return {
    ...promptForEditLeafSelect,
    childPrompts: {
      orderBy: { ord: 'asc' },
      select: {
        id: true,
        ord: true,
        prompt: {
          select: buildPromptForEditNestedSelect(nestedChildPromptDepth - 1),
        },
      },
    },
  };
}

/** ChildPrompt join select wrapping a nested prompt tree for bot read/edit payloads. */
export const botChildPromptForEditSelect = {
  orderBy: { ord: 'asc' as const },
  select: {
    id: true,
    ord: true,
    prompt: {
      select: buildPromptForEditNestedSelect(PROMPT_FOR_EDIT_CHILD_DEPTH),
    },
  },
} satisfies NonNullable<BotSelect['childPrompts']>;
